<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, markRaw, nextTick, onBeforeUnmount, onMounted, ref, shallowReactive, watch, type Component } from 'vue'
import { usePlayerStore } from '~/stores/player'
import ShellToasts from '~/components/mm/shell/ShellToasts.vue'
import ShellTopNav from '~/components/mm/shell/ShellTopNav.vue'
import ShellFooter from '~/components/mm/shell/ShellFooter.vue'
import ShellTabBar from '~/components/mm/shell/ShellTabBar.vue'
import PlayerDock from '~/components/mm/shell/PlayerDock.vue'
import MmDialog from '~/components/mm/base/MmDialog.vue'
import MmButton from '~/components/mm/base/MmButton.vue'
const { t } = useI18n()
const player = usePlayerStore()
const { request: paywallRequest, closePaywall } = usePaywall()
// Keep a loaded dialog mounted after its first open so close transitions and
// focus restoration retain their original lifecycle. Each user retry gets at
// most two attempts; neither a rejected nor a hung chunk traps the request.
function lazyPanel(loader: () => Promise<{ default: Component }>) {
  const state = shallowReactive({ component: null as Component | null, pending: false, failed: false, handingOff: false })
  let generation = 0
  let cancelAttempt: (() => void) | undefined
  const cancel = () => {
    ++generation
    cancelAttempt?.()
    cancelAttempt = undefined
    state.pending = false
    state.failed = false
    state.handingOff = false
  }
  const load = async () => {
    if (state.component || state.pending) return
    const owner = ++generation
    state.pending = true
    state.failed = false
    for (let attempt = 0; attempt < 2; attempt++) {
      let timer: ReturnType<typeof setTimeout> | undefined
      try {
        const module = await Promise.race([
          Promise.resolve().then(loader),
          new Promise<never>((_, reject) => {
            cancelAttempt = () => reject(new Error('Panel load cancelled'))
            timer = setTimeout(() => reject(new Error('Panel load timed out')), 10000)
          }),
        ])
        if (owner !== generation) return
        // Release the fallback dialog's focus/inert layer before mounting the
        // real dialog, so its focus owner is the original trigger.
        state.handingOff = true
        await nextTick()
        if (owner !== generation) return
        state.component = markRaw(module.default)
        state.pending = false
        state.handingOff = false
        return
      } catch {
        if (owner !== generation) return
      } finally {
        clearTimeout(timer)
        if (owner === generation) cancelAttempt = undefined
      }
    }
    state.pending = false
    state.failed = true
  }
  return { state, load, cancel }
}
const paywallPanel = lazyPanel(() => import('~/components/UpgradeDialog.vue'))
const nowPlayingPanel = lazyPanel(() => import('~/components/mm/shell/NowPlayingSheet.vue'))
watch(paywallRequest, request => { if (request) void paywallPanel.load(); else paywallPanel.cancel() }, { immediate: true, flush: 'sync' })
watch(() => player.nowPlayingOpen, open => { if (open) void nowPlayingPanel.load(); else nowPlayingPanel.cancel() }, { immediate: true, flush: 'sync' })
const panelFallbacks = computed(() => [
  { id: 'paywall', panel: paywallPanel, open: !!paywallRequest.value, title: t(paywallRequest.value?.trigger === 'download' ? 'paywall.downloadTitle' : 'paywall.creditsTitle'), close: closePaywall },
  { id: 'now-playing', panel: nowPlayingPanel, open: player.nowPlayingOpen, title: t('mm.shell.nowPlaying'), close: () => { player.nowPlayingOpen = false } },
])
useHead(() => ({ htmlAttrs: { style: `--live-h: ${player.shellHue}` } }))
const route = useRoute()
// This owner exists before the first chunk resolves. The mounted player may
// continue across routes as before; only its unresolved open intent expires.
watch(() => route.path, () => {
  closePaywall()
  if (!nowPlayingPanel.state.component) player.nowPlayingOpen = false
}, { flush: 'sync' })
const toolHues: Record<string, number> = { '/lyrics-generator': 46, '/rap-lyrics-generator': 14, '/rap-name-generator': 330 }
watch(() => route.path, path => player.setPageHue(toolHues[path.replace(/\/$/, '')]), { immediate: true })
const openMenus = useState('mm-open-menus', () => 0)
const keyboardOpen = ref(false)
const toastHeight = ref(0)
const updateKeyboard = () => {
  const viewport = window.visualViewport
  const editing = document.activeElement?.matches('input, textarea, select, [contenteditable="true"]')
  keyboardOpen.value = !!(viewport && editing && window.innerWidth < 1024 && window.innerHeight - viewport.height > 150)
}
onMounted(() => {
  window.visualViewport?.addEventListener('resize', updateKeyboard)
  document.addEventListener('focusin', updateKeyboard)
  document.addEventListener('focusout', updateKeyboard)
})
onBeforeUnmount(() => {
  closePaywall()
  if (!nowPlayingPanel.state.component) player.nowPlayingOpen = false
  paywallPanel.cancel()
  nowPlayingPanel.cancel()
  player.setPageHue()
  window.visualViewport?.removeEventListener('resize', updateKeyboard)
  document.removeEventListener('focusin', updateKeyboard)
  document.removeEventListener('focusout', updateKeyboard)
})
</script>

<template>
  <div class="app-shell" :style="{ '--toast-height': `${toastHeight}px` }" :class="{ 'has-played': player.hasPlayedThisSession, 'has-open-menu': openMenus > 0, 'has-player': player.visible, 'is-create': route.path === '/create', 'is-feed': route.path === '/feed', 'is-checkout': /^\/(checkout|payment)\//.test(route.path), 'keyboard-open': keyboardOpen }">
    <a class="skip" href="#main">{{ t('mm.shell.skip') }}</a>
    <div class="aura" aria-hidden="true" />
    <ShellTopNav />
    <main id="main" class="shell-content" tabindex="-1"><slot /></main>
    <ShellFooter />
    <ShellTabBar v-if="!/^\/(checkout|payment)\//.test(route.path)" />
    <PlayerDock :immersive="route.path === '/feed'" />
    <component :is="nowPlayingPanel.state.component" v-if="nowPlayingPanel.state.component" />
    <ShellToasts @resize="toastHeight = $event" />
    <component :is="paywallPanel.state.component" v-if="paywallPanel.state.component" />
    <MmDialog v-for="fallback in panelFallbacks" :key="fallback.id" :model-value="fallback.open && !fallback.panel.state.component && !fallback.panel.state.handingOff" :title="fallback.title" @close="fallback.close">
      <p v-if="fallback.panel.state.failed" role="alert">{{ t('common.load_failed') }}</p>
      <p v-else role="status">{{ t('common.loading') }}</p>
      <MmButton v-if="fallback.panel.state.failed" variant="secondary" @click="fallback.panel.load">{{ t('common.retry') }}</MmButton>
    </MmDialog>
  </div>
</template>
